import { SupportedChainId } from 'config/chains'
import { useQuoteProviders as useProvidersQuery } from 'hooks/backend/routing'
import { useConnection } from 'hooks/useConnection'
import { useCallback, useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { getProviderLogo } from 'state/routing/trades'

import {
  clearSelectedProviders,
  selectAllProviders as selectAllProvidersAction,
  setAvailableProviders,
  toggleProvider,
} from './actions'
import { LiquidityProvider } from './reducer'

// Off unless the user opts in: Kodiak's own Robinhood pools are still thin and its quote API is rate-limited
const PROVIDERS_OFF_BY_DEFAULT: Partial<Record<SupportedChainId, string[]>> = {
  [SupportedChainId.ROBINHOOD]: ['kodiak'],
}

const transformProvider = (provider: string): LiquidityProvider => ({
  id: provider.toLowerCase(),
  name: provider,
  imageUrl: getProviderLogo(provider.toLowerCase(), true),
})

export const useProviders = () => {
  const dispatch = useAppDispatch()
  const { chainId } = useConnection()
  const {
    selectedProviders: selectedProvidersFromState,
    availableProviders: availableProvidersFromState,
    allProviders,
  } = useAppSelector((state) => state.providers)

  const { isLoading, data, error } = useProvidersQuery()

  const transformedProviders = useMemo(() => data?.providers.map(transformProvider) ?? [], [data])

  // Each chain offers its own providers, so the list follows every chain switch
  useEffect(() => {
    if (transformedProviders.length > 0) {
      dispatch(setAvailableProviders({ providers: transformedProviders }))
    }
  }, [transformedProviders, dispatch])

  const defaultSelection = useMemo(() => {
    const offByDefault = PROVIDERS_OFF_BY_DEFAULT[chainId] ?? []
    const all = availableProvidersFromState.map((p) => p.id)
    const selection = all.filter((id) => !offByDefault.includes(id))
    // Never quote with no providers when the chain offers nothing but the ones switched off
    return selection.length > 0 ? selection : all
  }, [availableProvidersFromState, chainId])

  const selectedProviders = useMemo(
    () => (allProviders ?? true ? defaultSelection : selectedProvidersFromState),
    [allProviders, defaultSelection, selectedProvidersFromState]
  )

  const toggleProviderSelection = useCallback(
    (providerId: string) => dispatch(toggleProvider({ providerId, defaultSelection })),
    [dispatch, defaultSelection]
  )

  const clearProviderSelection = useCallback(() => dispatch(clearSelectedProviders()), [dispatch])

  const selectAllProviders = useCallback(() => dispatch(selectAllProvidersAction()), [dispatch])

  return {
    availableProviders: availableProvidersFromState,
    isLoading,
    error,
    selectedProviders,
    toggleProviderSelection,
    clearProviderSelection,
    selectAllProviders,
  }
}
